import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

const BASELINE_SCENARIO = { id: 'baseline', label: 'Baseline', color: '#2563eb' };

/**
 * Bass diffusion cumulative adoption: F(t) = (1 - e^(-(p+q)t)) / (1 + (q/p)e^(-(p+q)t))
 * t is scaled so curve spreads over [startYear, endYear] and approaches saturation (flattening) by endYear.
 */
export function generateBassCurve(startYear, endYear, p, q) {
  const years = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  const span = Math.max(1, endYear - startYear);
  // Use larger time multiplier (14) so F(t) approaches ~0.95+ by endYear, showing visible saturation
  const timeMultiplier = 14;
  return years.map((year) => {
    const t = ((year - startYear) / span) * timeMultiplier;
    const exp = Math.exp(-(p + q) * t);
    const F = (1 - exp) / (1 + (q / p) * exp);
    return { x: year, y: Math.min(100, Math.max(0, Number((F * 100).toFixed(2)))) };
  });
}

export function generateDefaultCurves(startYear, endYear) {
  return {
    baseline: generateBassCurve(startYear, endYear, 0.02, 0.32),
  };
}

const padding = { left: 56, right: 24, top: 20, bottom: 44 };

export function DraggableYearlyMultiLineChart({
  startYear,
  endYear,
  curves,
  onCurvesUpdate,
  xLabel = 'Year',
  yLabel = 'EV adoption (%)',
  height = 360,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [drag, setDrag] = useState(null); // { scenario, index }

  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const allPoints = useMemo(() => {
    const data = curves.baseline;
    if (!Array.isArray(data)) return [];
    return data.map((d) => d.y);
  }, [curves]);

  const minY = 0;
  const maxY = 100;

  const xScale = useCallback(
    (year) => {
      const range = endYear - startYear || 1;
      const t = (year - startYear) / range;
      return padding.left + t * chartWidth;
    },
    [startYear, endYear, chartWidth]
  );

  const yScale = useCallback(
    (y) => {
      const t = (y - minY) / (maxY - minY);
      return padding.top + chartHeight * (1 - t);
    },
    [chartHeight]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (typeof w === 'number') setWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const handleMouseDown = useCallback((scenario, index) => (e) => {
    e.preventDefault();
    setDrag({ scenario, index });
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!drag || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const dataY = (1 - (mouseY - padding.top) / chartHeight) * (maxY - minY) + minY;
      const clampedY = Math.max(0, Math.min(100, Number(dataY.toFixed(2))));
      const { scenario, index } = drag;
      const pts = curves[scenario];
      if (!pts || index < 0 || index >= pts.length) return;

      onCurvesUpdate({
        ...curves,
        [scenario]: pts.map((pt, i) => (i === index ? { ...pt, y: clampedY } : pt)),
      });
    },
    [drag, chartHeight, curves, onCurvesUpdate]
  );

  const handleMouseUp = useCallback(() => setDrag(null), []);
  const handleMouseLeave = useCallback(() => setDrag(null), []);

  const pathFor = (points) => {
    if (!Array.isArray(points) || points.length < 2) return '';
    let d = `M ${xScale(points[0].x)},${yScale(points[0].y)}`;
    for (let i = 1; i < points.length; i++) {
      d += ` L ${xScale(points[i].x)},${yScale(points[i].y)}`;
    }
    return d;
  };

  const years = useMemo(() => {
    const arr = [];
    for (let y = startYear; y <= endYear; y++) arr.push(y);
    return arr;
  }, [startYear, endYear]);

  const yTicks = [0, 25, 50, 75, 100];
  const xTickStep = Math.max(1, Math.ceil((endYear - startYear + 1) / 8));

  const draggedPoint = drag && curves.baseline && curves.baseline[drag.index];
  const dragLabel =
    draggedPoint != null
      ? `${draggedPoint.x}: ${Number(draggedPoint.y).toFixed(1)}%`
      : null;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  return (
    <div ref={containerRef} className="w-full font-ui" style={{ minHeight: height }}>
      <svg
        width="100%"
        height={height}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className="select-none"
      >
        {/* Grid */}
        {yTicks.slice(1, -1).map((y) => (
          <line
            key={y}
            x1={padding.left}
            y1={yScale(y)}
            x2={padding.left + chartWidth}
            y2={yScale(y)}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        ))}
        {years.filter((_, i) => i % xTickStep === 0).map((y) => (
          <line
            key={y}
            x1={xScale(y)}
            y1={padding.top}
            x2={xScale(y)}
            y2={padding.top + chartHeight}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
        ))}

        {/* Line and points (baseline only) */}
        {(() => {
          const points = curves.baseline;
          if (!Array.isArray(points) || points.length === 0) return null;
          const scenario = BASELINE_SCENARIO;
          return (
            <g key={scenario.id}>
              <path
                d={pathFor(points)}
                fill="none"
                stroke={scenario.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map((pt, index) => (
                <circle
                  key={`${scenario.id}-${index}`}
                  cx={xScale(pt.x)}
                  cy={yScale(pt.y)}
                  r={6}
                  fill={scenario.color}
                  stroke="#fff"
                  strokeWidth={2}
                  onMouseDown={handleMouseDown(scenario.id, index)}
                  className="cursor-pointer"
                />
              ))}
            </g>
          );
        })()}

        {/* X axis label */}
        <text
          x={padding.left + chartWidth / 2}
          y={height - 8}
          textAnchor="middle"
          className="text-sm fill-gray-600"
        >
          {xLabel}
        </text>
        {/* Y axis label */}
        <text
          transform={`rotate(-90) translate(${-height / 2}, 14)`}
          textAnchor="middle"
          className="text-sm fill-gray-600"
        >
          {yLabel}
        </text>
        {/* Y ticks */}
        {yTicks.map((y) => (
          <text
            key={y}
            x={padding.left - 8}
            y={yScale(y) + 4}
            textAnchor="end"
            className="text-xs fill-gray-500"
          >
            {y}
          </text>
        ))}
        {/* X ticks */}
        {years
          .filter((_, i) => i % xTickStep === 0)
          .map((y) => (
            <text
              key={y}
              x={xScale(y)}
              y={padding.top + chartHeight + 18}
              textAnchor="middle"
              className="text-xs fill-gray-500"
            >
              {y}
            </text>
          ))}

        {/* Drag readout: label near dragged point */}
        {dragLabel && draggedPoint && (() => {
          // Wider + clamped tooltip so it doesn't get cut off near edges.
          const tooltipWidth = 132;
          const tooltipHeight = 20;
          const ptX = xScale(draggedPoint.x);
          const ptY = yScale(draggedPoint.y);

          // Prefer above the point; if too close to top, place below.
          const preferredY = ptY - 28;
          const y = preferredY < padding.top ? ptY + 10 : preferredY;

          const xClamped = clamp(
            ptX - tooltipWidth / 2,
            padding.left,
            padding.left + chartWidth - tooltipWidth
          );
          const yClamped = clamp(
            y,
            padding.top,
            padding.top + chartHeight - tooltipHeight
          );

          return (
            <g>
              <rect
                x={xClamped}
                y={yClamped}
                width={tooltipWidth}
                height={tooltipHeight}
                rx={6}
                fill="#93c5fd"
                fillOpacity={0.35}
                stroke="#60a5fa"
                strokeOpacity={0.55}
              />
              <text
                x={xClamped + tooltipWidth / 2}
                y={yClamped + 14}
                textAnchor="middle"
                className="text-xs font-medium"
                fill="#0f172a"
              >
                {dragLabel}
              </text>
            </g>
          );
        })()}
      </svg>

      {/* Inline readout below chart when dragging */}
      {dragLabel && (
        <p className="text-sm text-gray-700 mt-1 text-center" aria-live="polite">
          Adjusting: {dragLabel}
        </p>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-6 mt-2 justify-center">
        <div className="flex items-center gap-2">
          <span
            className="w-4 h-0.5 rounded"
            style={{ backgroundColor: BASELINE_SCENARIO.color }}
          />
          <span className="text-sm font-medium text-gray-700">{BASELINE_SCENARIO.label}</span>
        </div>
      </div>
    </div>
  );
}

export default DraggableYearlyMultiLineChart;
